import { CoordinatorService } from './coordinator.service';
import { ScriptAttachment } from './script-attachment';
import { ScriptBase } from './script-base';
import { ScriptOverlap } from './script-overlap';
import type { FIRST_ARG, OVERLAP_ARG, SELECTION_ROLE, TEST_ARG } from './script-types';
import { STG } from './stage';

export class ScriptHitset extends ScriptBase {
    private _id: string;
    #coords: CoordinatorService;
    idSet = new Set<string>();
    payload?: any;
    parent?: ScriptBase;

    constructor(
        coords: CoordinatorService,
        public isPreview = false,
    ) {
        super(coords);
        this.#coords = coords;
        this._id = coords.uniqueId();
    }

    get target() {
        return this.parent?.target;
    }

    index(start: number, count?: number) {
        let asList = true;
        if (!count) {
            count = 1;
            asList = false;
        }
        if (start < 0) {
            start = this._dt.mainXitemLs.length + start;
        }
        const ls = Array.from(this.idSet)
            .slice(start, start + count)
            .map((id: string) => this._dt.get(id));

        return asList ? ls.map((x) => x?.scriptClass) : ls[0]?.scriptClass;
    }

    get mainXitemLs() {
        return this.#coords.dataset$.main.mainXitemLs.filter((x) => this.idSet.has(x.id));
    }

    get id() {
        return this._id;
    }

    get prefix() {
        const prefix = this.payload?.prefix;
        if (prefix) return prefix;
        return '';
    }

    get icon() {
        const icon = this.payload?.icon;
        if (icon) return icon;
        return 'hitset';
    }

    get title() {
        const t = this.payload?.title;
        if (t) return t;
        if (this.parent) return this.parent.title;
        return this.#coords.text('hitset');
    }

    private get _dt() {
        return this.isPreview ? this.#coords.dataset$.preview : this.#coords.dataset$.main;
    }

    get datasetIdLs() {
        return this._dt.mainXitemLs.map((xitm) => xitm.id);
    }

    get itemList() {
        return this._dt.mainXitemLs
            .filter((xitm) => this.idSet.has(xitm.id))
            .map((xitm) => xitm.scriptClass);
    }

    has(itmId: string) {
        return this.idSet.has(itmId);
    }

    hitset(isPreview = false) {
        const nH = new ScriptHitset(this.#coords, isPreview);
        if (this.isPreview === isPreview) return this.clone();
        return this._hitset(nH, this.idSet, this.isPreview);
    }

    clone() {
        const h = new ScriptHitset(this.#coords, this.isPreview);
        h.idSet = new Set(this.idSet);
        h.parent = this;
        return h;
    }

    attach(arg: ScriptAttachment) {
        arg.submit(Array.from(this.idSet));
    }

    sort(tp: string, isReverse = false) {
        const tpl = tp.split('-');
        tp = tpl[0];
        const ls = this.itemList.map((x) => {
            const itm: any = { itm: x };
            if (tp === 'random') itm.val = Math.random();
            else if (tp === 'weight') itm.val = x.weight;
            else {
                const val = x.get(tp);
                if (val === undefined) itm.val = undefined;
                else if (Array.isArray(val)) {
                    if (tpl[1] === 'last') {
                        itm.val = val[val.length - 1];
                    } else {
                        itm.val = val[0];
                    }
                } else {
                    itm.val = val;
                }
            }
            return itm;
        });

        ls.sort((a, b) => {
            if (a.val === undefined) return 1;
            if (b.val === undefined) return -1;
            if (typeof a.val === 'number') return a.val - b.val;
            else return a.val.localeCompare(b.val);
        });
        isReverse && ls.reverse();
        return ls.map((itm) => itm.itm);
    }

    overlap(target: ScriptHitset, isPreview = false) {
        return new ScriptOverlap(this.#coords, this, target, isPreview);
    }

    near(test: TEST_ARG, n: number) {
        console.log(test, n);
        return this;
    }

    follow(test: TEST_ARG, n: number) {
        console.log(test, n);
        return this;
    }

    and(arg: FIRST_ARG, test?: TEST_ARG) {
        let h = new ScriptHitset(this.#coords, this.isPreview);
        if (arg instanceof Function || arg instanceof RegExp) {
            test = arg;
            arg = 'text';
        }
        if (!test && typeof arg === 'string') {
            test = arg;
            arg = 'text';
        }

        if (test) {
            // this.#coords.highlight$.reportTest(test);
            const rLs = this._dt
                .xitemLsFromIdLs(Array.from(this.idSet))
                .filter((xitm) => xitm.hit(arg as string, test));
            h.idSet = new Set(rLs.map((xitm) => xitm.id));
            // this._dt.xitemLsFromIdLs(Array.from(this.idSet)).forEach((xitm) => {
            //     if (xitm.hit(arg as string, test)) h.idSet.add(xitm.id);
            // });
            return h;
        }
        if (!Array.isArray(arg)) {
            arg = [arg];
        }
        if (!arg.length) return h;
        if (typeof arg[0] === 'string') {
            const s = new Set<string>();
            (arg as string[]).forEach((x) => this.idSet.has(x) && s.add(x));
            h.idSet = s;
            return h;
        }
        h = this.clone();
        arg.forEach((x) => {
            if (typeof x === 'string') return;
            const s = x instanceof ScriptHitset ? x.idSet : x.hitset(this.isPreview).idSet;
            Array.from(h.idSet).forEach((y) => !s.has(y) && h.idSet.delete(y));
        });
        return h;
    }
    or(arg: FIRST_ARG, test?: TEST_ARG) {
        let h = this.clone();
        if (arg instanceof Function || arg instanceof RegExp) {
            test = arg;
            arg = 'text';
        }
        if (!test && typeof arg === 'string') {
            test = arg;
            arg = 'text';
        }

        if (test) {
            // this.#coords.highlight$.reportTest(test);
            this._dt.mainXitemLs.forEach((xitm) => {
                if (xitm.hit(arg as string, test)) h.idSet.add(xitm.id);
            });
            return h;
        }
        if (!Array.isArray(arg)) {
            arg = [arg];
        }
        if (!arg.length) return h;
        if (typeof arg[0] === 'string') {
            arg.forEach((x) => h.idSet.add(x as string));
        }
        arg.forEach((x) => {
            if (typeof x === 'string') return;
            const s = x instanceof ScriptHitset ? x.idSet : x.hitset(this.isPreview).idSet;
            s.forEach((y) => h.idSet.add(y));
        });
        return h;
    }

    add(arg: FIRST_ARG, test?: TEST_ARG) {
        const h = this.or(arg, test);
        this.idSet = h.idSet;
        return this;
    }

    remove(arg: FIRST_ARG, test?: TEST_ARG) {
        const h = this.not(arg, test);
        this.idSet = h.idSet;
        return this;
    }

    not(arg: FIRST_ARG, test?: TEST_ARG) {
        let h = this.clone();
        if (arg instanceof Function || arg instanceof RegExp) {
            test = arg;
            arg = 'text';
        }
        if (!test && typeof arg === 'string') {
            test = arg;
            arg = 'text';
        }

        if (test) {
            // this.#coords.highlight$.reportTest(test);
            const rLs = this._dt
                .xitemLsFromIdLs(Array.from(this.idSet))
                .filter((xitm) => !xitm.hit(arg as string, test));
            h.idSet = new Set(rLs.map((xitm) => xitm.id));
            return h;
        }
        if (!Array.isArray(arg)) {
            arg = [arg];
        }
        if (!arg.length) return h;
        if (typeof arg[0] === 'string') {
            arg.forEach((x) => h.idSet.delete(x as string));
        }
        arg.forEach((x) => {
            if (typeof x === 'string') return;
            const s = x instanceof ScriptHitset ? x.idSet : x.hitset(this.isPreview).idSet;
            s.forEach((y) => h.idSet.delete(y));
        });
        return h;
    }

    extract(tag: string, withCount = false) {
        if (tag === 'has' || tag === 'doctype') withCount = true;

        let isFirst = tag.endsWith('-first');
        let isLast = tag.endsWith('-last');

        if (withCount) {
            const mp = new Map<string, number>();
            if (tag === 'doctype') {
                const countMap = new Map<string, number>();
                this.itemList.map((xitm) => {
                    if (xitm.has('patent')) {
                        let c = countMap.get('patent') || 0;
                        countMap.set('patent', c + 1);
                    } else {
                        const pLs = xitm.get('publicationType') || [];
                        pLs.forEach((pt: string) => {
                            let c = countMap.get(pt) || 0;
                            countMap.set(pt, c + 1);
                        });
                    }
                });
                countMap.forEach((c, k) => mp.set(k, c));
            } else if (tag === 'has') {
                const withSubsetSet = new Set<string>();

                this.#coords.view$.viewLs.forEach((view) => {
                    view.idSet.forEach((id) => withSubsetSet.add(id));
                });

                let doiCount = 0;
                let attachCount = 0;
                let commentCount = 0;
                let subsetCount = 0;
                let duplCount = 0;

                this.itemList.map((xitm) => {
                    if (xitm.get('doi')) doiCount++;
                    if (xitm.has('attachment')) attachCount++;
                    if (xitm.has('comment')) commentCount++;
                    if (withSubsetSet.has(xitm.id)) subsetCount++;
                    if (xitm.has('duplicate')) duplCount++;
                });
                doiCount && mp.set('doi', doiCount);
                attachCount && mp.set('attachment', attachCount);
                commentCount && mp.set('comment', commentCount);
                subsetCount && mp.set('subset', subsetCount);
                duplCount && mp.set('duplicate', duplCount);
            } else if (tag === 'keyword') {
                this.#coords.keyword$.keywordLs.forEach((kw) => {
                    mp.set(kw.source, kw.size(this.isPreview ? 'preview' : 'main'));
                });
            } else {
                this.itemList.map((xitm) => {
                    let valLs = xitm.get(tag);
                    if (!valLs) return;

                    if (!Array.isArray(valLs)) valLs = [valLs.toString()];
                    else if (!valLs.length) return;
                    if (isFirst) valLs = [valLs[0]];
                    else if (isLast) valLs = [valLs[valLs.length - 1]];

                    valLs.forEach((val: any) => {
                        let n = mp.get(val);
                        mp.set(val, n ? n + 1 : 1);
                    });
                });
            }
            const ls = Array.from(mp.entries());
            ls.sort((a, b) => b[1] - a[1]);
            return ls;
        } else {
            let ls: any = [];
            if (tag === 'keyword') {
                this.#coords.keyword$.keywordLs.forEach((kw) => {
                    if (kw.idSet(this.isPreview ? 'preview' : 'main').size) ls.push(kw.source);
                });
            } else {
                ls = Array.from(new Set(this.itemList.map((x) => x.get(tag)).filter((x) => x)));
            }
            ls.sort((a: any, b: any) => a.localeCompare(b));
            return ls;
        }
    }

    display() {
        this.#coords.search$.clearAll();
        const stg = this.#coords.stage$.current.tag;

        this.#coords.resultList$.activateSubset({
            idSet: this.idSet,
            title: this.title,
            prefix: this.prefix,
            icon: this.icon,
            class: 'selection-in-general',
            back: () => this.#coords.stage$.set(stg),
            stage: this.isPreview ? 'preview' : 'collection',
        });

        return this;
    }

    field(
        title: string,
        checkSize?: boolean,
    ): { size: number; preview: () => void; title: string } {
        let size: number;
        if (checkSize) {
            const dt = this.isPreview ? this.#coords.dataset$.preview : this.#coords.dataset$.main;
            size = dt.existingIdSet(this.idSet).size;
        } else size = this.size;
        return {
            size,
            preview: () => this.display(),
            title: this.#coords.text(title),
        };
    }

    log(prefix = 'Hitset') {
        console.log(
            `${prefix}: ${this.idSet.size} items in ${this.isPreview ? 'preview' : 'collection'}`,
        );
        return this;
    }

    get size() {
        return this.idSet.size;
    }
}
